import { useCallback } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@/store/store";
import { runSafeQuery, isAbortLikeError } from "@/hooks/use-safe-query";
import { menuCatalogQueryKey } from "@/query-keys/menu.keys";
import {
  invalidateMenuCatalogCache,
  isMenuCatalogFetchCompleted,
  markMenuCatalogFetchCompleted,
} from "@/services/client-query-cache";
import {
  mapApiMenuToMenuItemV2,
  mapMenuItemV2ToLegacyShape,
  type ApiMenuCatalogItemRaw,
  type MenuItemV2WithCategory,
} from "@/adapters/menuV2Adapter";
import { buildMenuCatalogSnapshot } from "@/domain/cart/menu-catalog.snapshot";
import {
  getLatestMenuCatalogSnapshot,
  setLatestMenuCatalogSnapshot,
} from "@/domain/cart/menu-catalog.registry";
import { updateMenuItems } from "@/slices/menu-items-slice";
import { readSafeCartItems } from "@/store/cart/cart.selectors";
import API from "@/utils/api";
import { MenuItemInterface } from "@/models/menu-item";
import { getRestaurantId, usesHostOnlyPublicPaths } from "@/config/restaurant-id";
import { WEBSITE_ORDER_CHANNEL } from "@/constants/orderChannel";
import { usePublicTenant } from "@/context/PublicTenantProvider";
import { buildStorefrontMenuCatalogPath } from "@/services/ensure-catalog-loaded";
import {
  logAuthTokenDiag,
  logMenuDiag,
} from "@/utils/menu-load-diagnostics";

export { invalidateMenuCatalogCache, getLatestMenuCatalogSnapshot };
export { buildStorefrontMenuCatalogPath };

/** Normalize catalog snapshot response body to a row array (envelope or raw JSON). */
function extractCatalogSnapshotRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data;
  }
  return [];
}

function getErrorLogMessage(e: unknown): string | null {
  if (e == null) return null;
  if (e instanceof Error) return e.message;
  return String(e);
}

export function useMenuItems() {
  const dispatch = useDispatch();
  const { slug: tenantSlug } = usePublicTenant();

  const fetchMenuItems = useCallback(
    (options?: { force?: boolean }) =>
      fetchMenuCatalogItems(dispatch, {
        ...options,
        slug: (tenantSlug ?? "").trim(),
      }),
    [dispatch, tenantSlug]
  );

  return { fetchMenuItems, getCatalogSnapshot: getLatestMenuCatalogSnapshot };
}

/** Imperative menu catalog fetch — exported for tests and hook wrapper. */
export async function fetchMenuCatalogItems(
  dispatch: AppDispatch,
  options?: { force?: boolean; slug?: string }
): Promise<void> {
    const restaurantId = getRestaurantId().trim();
    const slug = (options?.slug ?? "").trim();
    if (!restaurantId) {
      logMenuDiag("fetch_menu_items_skipped", {
        reason: "restaurant_id_not_resolved",
      });
      return;
    }
    if (!slug && !usesHostOnlyPublicPaths()) {
      logMenuDiag("fetch_menu_items_skipped", {
        reason: "platform_slug_not_resolved",
      });
      return;
    }

    const key = menuCatalogQueryKey(restaurantId);
    const force = Boolean(options?.force);
    const catalogCompleted = isMenuCatalogFetchCompleted(key);
    const catalogPath = buildStorefrontMenuCatalogPath(slug);

    logMenuDiag("fetch_menu_items_called", {
      force,
      catalogCompleted,
      queryKey: key,
      menuRestaurantId: restaurantId,
      configRestaurantId: getRestaurantId(),
      restaurantIdMatch: true,
    });
    logAuthTokenDiag("fetch_menu_items");

    if (!force && catalogCompleted) {
      logMenuDiag("fetch_menu_items_skipped", {
        reason: "catalog_fetch_already_completed",
        queryKey: key,
      });
      return;
    }

    logMenuDiag("menu_request_start", {
      queryKey: key,
      catalogPath,
      force,
    });

    try {
      await runSafeQuery(
      key,
      async (signal) => {
        const cartLines = readSafeCartItems();
        try {
          dispatch(updateMenuItems({ fetching: true }));

          let v2Error: Error | null = null;
          let mappingFailed = false;
          let menuItemsV2: MenuItemV2WithCategory[] = [];
          const catalogResult = await (async () => {
            try {
              return await API.get(catalogPath, {
                headers: { Accept: "application/json" },
                signal,
              });
            } catch (e) {
              v2Error = e instanceof Error ? e : new Error(String(e));
              return null;
            }
          })();

          if (v2Error == null && catalogResult) {
            try {
              const rawRows = extractCatalogSnapshotRows(catalogResult.data);
              menuItemsV2 = rawRows.map((row) => mapApiMenuToMenuItemV2(row as ApiMenuCatalogItemRaw));
            } catch (e) {
              mappingFailed = true;
              console.error("[useMenuItems] catalog snapshot mapping failed", e);
            }
          }

          const catalogOk = v2Error === null && !mappingFailed;
          if (!catalogOk) {
            const v2ErrorMessage: string | null = getErrorLogMessage(v2Error);
            console.error("[useMenuItems] catalog snapshot request failed", {
              error: v2ErrorMessage,
              mappingFailed,
            });
            const emptyCatalog = buildMenuCatalogSnapshot({
              items: [],
              restaurantId,
              restaurantAvailable: false,
              loaded: false,
            });
            setLatestMenuCatalogSnapshot(emptyCatalog);
            dispatch(updateMenuItems({ list: [], catalogSnapshot: emptyCatalog }));
            logMenuDiag("menu_request_failure", {
              queryKey: key,
              reason: "catalog_not_ok",
              error: v2ErrorMessage,
              mappingFailed,
            });
            return false;
          }

          let listForRedux: MenuItemInterface[] = [];

          if (menuItemsV2.length !== 0) {
            const convertedList: MenuItemInterface[] = menuItemsV2
              .filter((m) => !m.is_deleted)
              .map((v2) => {
                const base = mapMenuItemV2ToLegacyShape(v2, { orderChannel: WEBSITE_ORDER_CHANNEL });
                const cartItem = cartLines?.find((item) => item.menu_item_id === base.menu_item_id);
                return {
                  ...base,
                  count: cartItem?.quantity ?? 0,
                  isFavorite: false,
                };
              });

            listForRedux = convertedList;
          }

          const catalogSnapshot = buildMenuCatalogSnapshot({
            items: listForRedux,
            restaurantId,
            restaurantAvailable: true,
            loaded: true,
          });
          setLatestMenuCatalogSnapshot(catalogSnapshot);

          dispatch(
            updateMenuItems({
              list: listForRedux,
              catalogSnapshot,
            })
          );
          logMenuDiag("menu_request_success", {
            queryKey: key,
            itemCount: listForRedux.length,
          });
          markMenuCatalogFetchCompleted(key);
          logMenuDiag("menu_catalog_mark_completed_success", {
            queryKey: key,
            itemCount: listForRedux.length,
          });
          return true;
        } catch (e) {
          console.error("[useMenuItems] unexpected error while loading catalog snapshot");
          logMenuDiag("menu_request_failure", {
            queryKey: key,
            reason: "unexpected_error",
            error: getErrorLogMessage(e),
          });
          const emptyCatalog = buildMenuCatalogSnapshot({
            items: [],
            restaurantId,
            restaurantAvailable: false,
            loaded: false,
          });
          setLatestMenuCatalogSnapshot(emptyCatalog);
          dispatch(updateMenuItems({ list: [], catalogSnapshot: emptyCatalog }));
          return false;
        } finally {
          dispatch(updateMenuItems({ fetching: false }));
        }
      },
      { cache: "none", force }
    );
    } catch (e) {
      if (!isAbortLikeError(e)) {
        throw e;
      }
    }
}

